import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { BookingDto, SalonWorkingHoursResponse, AppAbsenceDto } from '@api/generated';
import { useToast } from '@/hooks/useToast';
import { bookingsWriteApi } from '@/api/api';

type CalendarViewMode = 'day' | 'week' | 'fullWeek';
type CalendarDisplayMode = 'normal' | 'condensed';

interface CustomBookingCalendarProps {
  bookings: BookingDto[];
  selectedDate: string;
  isLoading: boolean;
  workingHours?: SalonWorkingHoursResponse[];
  absences?: AppAbsenceDto[]; // Přidáme prop pro absence
  staffList?: Array<{ id: string; name: string }>; // Přidáme prop pro seznam zaměstnanců
  onEmptySlotClick?: (time: string, staffName: string) => void;
  onDateChange?: (direction: 'prev' | 'next') => void;
  onTodayClick?: () => void;
  viewMode?: CalendarViewMode;
  onViewModeChange?: (mode: CalendarViewMode) => void;
  displayMode?: CalendarDisplayMode;
  onDisplayModeChange?: (mode: CalendarDisplayMode) => void;
  onBookingMoved?: () => void; // Callback pro aktualizaci po přesunu
}

const CustomBookingCalendar: React.FC<CustomBookingCalendarProps> = ({
  bookings,
  selectedDate,
  isLoading,
  workingHours = [],
  absences = [],
  staffList = [],
  onEmptySlotClick,
  onDateChange,
  onTodayClick,
  viewMode = 'day',
  onViewModeChange,
  displayMode = 'normal',
  onDisplayModeChange,
  onBookingMoved,
}) => {
  // Použij reálná data z API
  const displayBookings = bookings;

  // Drag & Drop state
  const [draggedBooking, setDraggedBooking] = useState<BookingDto | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{ time: string; staffName: string; day: dayjs.Dayjs } | null>(null);
  const [, setIsDragging] = useState(false);
  const toast = useToast();

  // Drag & Drop funkce
  const handleDragStart = (e: React.DragEvent, booking: BookingDto) => {
    setDraggedBooking(booking);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.id);
  };

  const handleDragEnd = () => {
    setDraggedBooking(null);
    setDragOverSlot(null);
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent, time: string, staffName: string, day: dayjs.Dayjs) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ time, staffName, day });
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = async (e: React.DragEvent, time: string, staffName: string, day: dayjs.Dayjs) => {
    e.preventDefault();

    if (!draggedBooking) return;

    try {
      // Zkontrolujeme, jestli je nový slot v otevíracích hodinách
      const dayOfWeek = day.day();
      // Převod z dayjs formátu (0-6) na API formát (1-7)
      const apiWeekday = dayOfWeek === 0 ? 7 : dayOfWeek;
      const dayWorkingHours = workingHours.filter(wh => wh.weekday === apiWeekday && wh.isActive);

      if (dayWorkingHours.length === 0) {
        toast.error('Chyba!', 'Salon je v tento den zavřený');
        return;
      }

      // Zkontrolujeme, jestli je slot blokovaný absencí
      const blockingAbsence = getBlockingAbsence(day, time, staffName);
      if (blockingAbsence) {
        toast.error('Chyba!', `Slot je blokovaný absencí: ${blockingAbsence.name}`);
        return;
      }

      // Zkontrolujeme, jestli je čas v otevíracích hodinách
      const isWithinWorkingHours = dayWorkingHours.some(wh =>
        wh.intervals?.some(interval => {
          if (!interval.startTime || !interval.endTime || !interval.isActive) return false;

          const [startHour, startMinute] = interval.startTime.split(':').map(Number);
          const [endHour, endMinute] = interval.endTime.split(':').map(Number);
          const [slotHour, slotMinute] = time.split(':').map(Number);

          const slotTimeMinutes = slotHour * 60 + slotMinute;
          const startTimeMinutes = startHour * 60 + startMinute;
          const endTimeMinutes = endHour * 60 + endMinute;

          return slotTimeMinutes >= startTimeMinutes && slotTimeMinutes < endTimeMinutes;
        }),
      );

      if (!isWithinWorkingHours) {
        toast.error('Chyba!', 'Čas je mimo otevírací hodiny');
        return;
      }

      // Zkontrolujeme, jestli je zaměstnanec dostupný v novém čase
      const newDateTime = day.format('YYYY-MM-DD');
      const conflictingBookings = bookings.filter(booking => {
        if (!booking.start || !booking.worker?.displayName) return false;

        const bookingDate = dayjs(booking.start).format('YYYY-MM-DD');
        const bookingTime = dayjs(booking.start).format('HH:mm');
        const bookingStaff = booking.worker.displayName;

        return bookingDate === newDateTime && bookingTime === time && bookingStaff === staffName && booking.id !== draggedBooking.id;
      });

      if (conflictingBookings.length > 0) {
        toast.error('Chyba!', 'Zaměstnanec má v tomto čase jinou rezervaci');
        return;
      }

      // Najdeme ID nového zaměstnance
      const newStaffMember = staffList.find(staff => staff.name === staffName);
      const originalStaffName = draggedBooking.worker?.displayName;

      // Pokud se mění zaměstnanec, zkontrolujeme dostupnost
      if (newStaffMember && originalStaffName !== staffName) {
        // Zde bychom mohli přidat kontrolu, jestli nový zaměstnanec může realizovat službu
        // Prozatím pouze logujeme
        console.log(`Přesun rezervace od ${originalStaffName} k ${staffName}`);
      }

      // Připravíme move request
      const [hours, minutes] = time.split(':').map(Number);
      const moveRequest: {
        newDate: string;
        newStartTime: string;
        notes: string;
        newWorkerId?: string;
      } = {
        newDate: newDateTime,
        newStartTime: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
        notes: `Přesunuto z ${dayjs(draggedBooking.start).format('DD.MM.YYYY HH:mm')}`,
      };

      // Pokud se mění zaměstnanec, přidáme newWorkerId
      if (newStaffMember && originalStaffName !== staffName) {
        moveRequest.newWorkerId = newStaffMember.id;
        moveRequest.notes += ` (přesunuto od ${originalStaffName} k ${staffName})`;
      }

      await bookingsWriteApi.moveBooking({
        bookingId: draggedBooking.id,
        moveBookingRequest: moveRequest,
      });

      // Toast zpráva podle typu přesunu
      if (originalStaffName !== staffName) {
        toast.success('Úspěch!', `Rezervace byla přesunuta na ${day.format('DD.MM.YYYY')} v ${time} k ${staffName}`);
      } else {
        toast.success('Úspěch!', `Rezervace byla přesunuta na ${day.format('DD.MM.YYYY')} v ${time}`);
      }

      // Aktualizujeme data
      if (onBookingMoved) {
        onBookingMoved();
      }
    } catch (error) {
      console.error('Chyba při přesunu rezervace:', error);
      toast.error('Chyba!', 'Nepodařilo se přesunout rezervaci');
    } finally {
      setDraggedBooking(null);
      setDragOverSlot(null);
      setIsDragging(false);
    }
  };

  // Vytvoříme seznam zaměstnanců - kombinace z rezervací a předaného seznamu
  const displayEmployees = useMemo(() => {
    // Zaměstnanci z rezervací
    const staffFromBookings = [...new Set(bookings.map(booking => booking.worker?.displayName).filter(Boolean))];

    // Zaměstnanci z předaného seznamu
    const staffFromList = staffList.map(staff => staff.name);

    // Kombinujeme oba seznamy a odstraníme duplicity
    const allStaffNames = [...new Set([...staffFromBookings, ...staffFromList])];

    console.log('Zaměstnanci z rezervací:', staffFromBookings);
    console.log('Zaměstnanci z seznamu:', staffFromList);
    console.log('Všichni zaměstnanci:', allStaffNames);

    return allStaffNames.map((staffName, index) => ({
      id: `staff-${index}`,
      displayName: staffName,
      status: 'ACTIVE' as const,
    }));
  }, [bookings, staffList]);

  // Generujeme dny podle režimu zobrazení
  const displayDays = useMemo(() => {
    const startDate = dayjs(selectedDate);

    switch (viewMode) {
      case 'day':
        return [startDate];

      case 'week': {
        // Týden bez zavřených dnů - pouze dny s otevíracími hodinami
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
          const day = startDate.startOf('week').add(i, 'day');
          const dayOfWeek = day.day();
          // Převod z dayjs formátu (0-6) na API formát (1-7)
          const apiWeekday = dayOfWeek === 0 ? 7 : dayOfWeek;
          const hasWorkingHours = workingHours.some(wh => wh.weekday === apiWeekday && wh.isActive);

          if (hasWorkingHours) {
            weekDays.push(day);
          }
        }
        return weekDays;
      }

      case 'fullWeek': {
        // Celý kalendářní týden
        const fullWeekDays = [];
        for (let i = 0; i < 7; i++) {
          fullWeekDays.push(startDate.startOf('week').add(i, 'day'));
        }
        return fullWeekDays;
      }

      default:
        return [startDate];
    }
  }, [selectedDate, viewMode, workingHours]);

  // Generujeme časové sloty podle otevíracích hodin
  const timeSlots = useMemo(() => {
    console.log('🔍 Debug otevíracích hodin:');
    console.log('- Vybraný den:', selectedDate, 'Režim zobrazení:', viewMode);
    console.log('- Všechny otevírací hodiny:', workingHours);

    // Debug: zobrazíme všechny pracovní hodiny s detaily
    workingHours.forEach(wh => {
      console.log(`📅 Den ${wh.weekday} (${wh.weekdayName}):`, {
        isActive: wh.isActive,
        intervals: wh.intervals?.map(i => `${i.startTime}-${i.endTime} (${i.isActive ? 'aktivní' : 'neaktivní'})`),
      });
    });

    // Pro týdenní zobrazení potřebujeme najít všechny aktivní dny v týdnu
    let activeDays: number[] = [];

    if (viewMode === 'week' || viewMode === 'fullWeek') {
      // Pro týdenní zobrazení najdeme všechny aktivní dny v týdnu
      for (let i = 0; i < 7; i++) {
        const day = dayjs(selectedDate).startOf('week').add(i, 'day');
        const dayOfWeek = day.day();
        const apiWeekday = dayOfWeek === 0 ? 7 : dayOfWeek;
        const hasWorkingHours = workingHours.some(wh => wh.weekday === apiWeekday && wh.isActive);

        if (hasWorkingHours) {
          activeDays.push(apiWeekday);
        }
      }

      console.log('- Aktivní dny v týdnu:', activeDays);
    } else {
      // Pro denní zobrazení použijeme pouze vybraný den
      const selectedDayOfWeek = dayjs(selectedDate).day();
      const apiWeekday = selectedDayOfWeek === 0 ? 7 : selectedDayOfWeek;
      activeDays = [apiWeekday];

      console.log('- Vybraný den v týdnu:', apiWeekday);
    }

    // Najdeme otevírací hodiny pro všechny aktivní dny
    const allWorkingHours = workingHours.filter(wh => activeDays.includes(wh.weekday) && wh.isActive);

    console.log('- Otevírací hodiny pro aktivní dny:', allWorkingHours);

    if (allWorkingHours.length === 0) {
      console.log('⚠️ Žádné otevírací hodiny pro aktivní dny - salon je zavřený');
      // Pokud nejsou otevírací hodiny, salon je zavřený - všechny sloty jsou nedostupné
      const slots = [];
      for (let hour = 7; hour < 22; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const displayTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          slots.push({ time, displayTime, hour, minute, isWithinWorkingHours: false });
        }
      }
      return slots;
    }

    // Najdeme min a max čas z otevíracích hodin
    let minHour = 23,
      maxHour = 0;
    let minMinute = 59,
      maxMinute = 0;

    allWorkingHours.forEach(wh => {
      if (wh.intervals && wh.intervals.length > 0) {
        wh.intervals.forEach(interval => {
          if (interval.startTime && interval.endTime && interval.isActive) {
            const [startHour, startMinute] = interval.startTime.split(':').map(Number);
            const [endHour, endMinute] = interval.endTime.split(':').map(Number);

            console.log(`📅 Otevírací hodiny: ${interval.startTime} - ${interval.endTime} (${wh.weekdayName})`);

            if (startHour < minHour || (startHour === minHour && startMinute < minMinute)) {
              minHour = startHour;
              minMinute = startMinute;
            }

            if (endHour > maxHour || (endHour === maxHour && endMinute > maxMinute)) {
              maxHour = endHour;
              maxMinute = endMinute;
            }
          }
        });
      }
    });

    console.log(
      `⏰ Vypočítaný rozsah: ${minHour.toString().padStart(2, '0')}:${minMinute.toString().padStart(2, '0')} - ${maxHour.toString().padStart(2, '0')}:${maxMinute
        .toString()
        .padStart(2, '0')}`,
    );

    // Generujeme sloty od min do max času
    const slots = [];
    let currentHour = minHour;
    let currentMinute = minMinute;

    while (currentHour < maxHour || (currentHour === maxHour && currentMinute <= maxMinute)) {
      const time = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
      const displayTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;

      // Zkontrolujeme, jestli je tento čas v otevíracích hodinách
      const isWithinWorkingHours = allWorkingHours.some(wh =>
        wh.intervals?.some(interval => {
          if (!interval.startTime || !interval.endTime || !interval.isActive) return false;

          const [startHour, startMinute] = interval.startTime.split(':').map(Number);
          const [endHour, endMinute] = interval.endTime.split(':').map(Number);

          const currentTimeMinutes = currentHour * 60 + currentMinute;
          const startTimeMinutes = startHour * 60 + startMinute;
          const endTimeMinutes = endHour * 60 + endMinute;

          const isWithin = currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < endTimeMinutes;

          // Debug log pro první slot
          if (currentHour === minHour && currentMinute === minMinute) {
            console.log(`🔍 Kontrola otevíracích hodin pro ${time}:`, {
              interval: `${interval.startTime} - ${interval.endTime}`,
              currentTimeMinutes,
              startTimeMinutes,
              endTimeMinutes,
              isWithin,
            });
          }

          return isWithin;
        }),
      );

      // Debug log pro výsledek
      if (currentHour === minHour && currentMinute === minMinute) {
        console.log(`📊 Výsledek pro ${time}: isWithinWorkingHours = ${isWithinWorkingHours}`);
      }

      slots.push({
        time,
        displayTime,
        hour: currentHour,
        minute: currentMinute,
        isWithinWorkingHours,
      });

      // Přidáme 15 minut
      currentMinute += 15;
      if (currentMinute >= 60) {
        currentMinute = 0;
        currentHour++;
      }
    }

    return slots;
  }, [selectedDate, workingHours]);

  // Získáváme rezervace pro konkrétního zaměstnance a den
  const getBookingsForEmployee = (employeeName: string, day: dayjs.Dayjs) => {
    const dayString = day.format('YYYY-MM-DD');
    const filtered = displayBookings.filter(booking => {
      if (!booking.worker?.displayName || !booking.start) return false;

      // Zkontrolujeme, jestli rezervace patří do tohoto dne
      const bookingDate = dayjs(booking.start).format('YYYY-MM-DD');
      const isEmployeeMatch = booking.worker.displayName === employeeName;
      const isDateMatch = bookingDate === dayString;

      return isEmployeeMatch && isDateMatch;
    });

    console.log(`Hledám rezervace pro "${employeeName}" v den ${dayString}:`, filtered);

    // Debug logování pro serviceColor
    filtered.forEach(booking => {
      if (booking.bookingLines && booking.bookingLines.length > 0) {
        console.log('🔍 Debug rezervace:', {
          bookingId: booking.id,
          serviceName: booking.bookingLines[0]?.nameSnapshot,
          serviceColor: booking.bookingLines[0]?.serviceColor,
          bookingLines: booking.bookingLines,
        });
      }
    });

    return filtered;
  };

  // Určíme barvu podle služby (pokud je k dispozici) nebo stavu rezervace
  const getBookingColor = (booking: BookingDto) => {
    // Pokud má rezervace barvu služby, použijeme ji
    const serviceColor = booking.bookingLines?.[0]?.serviceColor;
    if (serviceColor) {
      return `opacity-90`;
    }

    // Fallback na stav rezervace
    switch (booking.state) {
      case 'CONFIRMED':
        return 'bg-green-500 border-green-600';
      case 'PENDING':
        return 'bg-yellow-500 border-yellow-600';
      case 'IN_SERVICE':
        return 'bg-purple-500 border-purple-600';
      case 'DONE':
        return 'bg-gray-500 border-gray-600';
      case 'CANCELED':
        return 'bg-red-500 border-red-600';
      case 'NO_SHOW':
        return 'bg-orange-500 border-orange-600';
      default:
        return 'bg-blue-500 border-blue-600';
    }
  };

  // Získáme barvu služby pro rezervaci
  const getServiceColor = (booking: BookingDto) => {
    const serviceColor = booking.bookingLines?.[0]?.serviceColor;
    if (serviceColor) {
      console.log('🎨 ServiceColor pro rezervaci:', {
        bookingId: booking.id,
        serviceName: booking.bookingLines?.[0]?.nameSnapshot,
        serviceColor: serviceColor,
      });
    }
    return serviceColor;
  };

  // Formátujeme čas pro zobrazení
  const formatTime = (dateString: string) => {
    if (!dateString) return '00:00';
    try {
      return dayjs(dateString).format('HH:mm');
    } catch (error) {
      console.error('Chyba při formátování času:', dateString, error);
      return '00:00';
    }
  };

  // Určíme styl pro den - pouze označíme zavřené dny
  const getDayStyle = (day: dayjs.Dayjs) => {
    const dayOfWeek = day.day();
    const apiWeekday = dayOfWeek === 0 ? 7 : dayOfWeek;
    const dayWorkingHours = workingHours.filter(wh => wh.weekday === apiWeekday && wh.isActive);

    if (dayWorkingHours.length === 0) {
      return {
        headerBg: 'bg-gray-100',
        headerText: 'text-gray-500',
        borderColor: 'border-gray-200',
        statusText: 'Zavřeno',
        statusColor: 'text-gray-500',
      };
    }

    // Standardní styl pro otevřené dny
    return {
      headerBg: 'bg-gray-100',
      headerText: 'text-gray-900',
      borderColor: 'border-gray-200',
      statusText: 'Otevřeno',
      statusColor: 'text-gray-600',
    };
  };

  // Určíme styl buňky - zelené podbarvení pouze pro dostupné sloty
  const getCellStyle = (isAvailable: boolean = false) => {
    // Jemné zelené podbarvení pouze pro dostupné sloty
    return isAvailable ? 'bg-green-50 border-gray-100' : 'bg-white border-gray-100';
  };

  // Určíme výšku řádků podle display mode
  const getRowHeight = () => {
    return displayMode === 'condensed' ? 32 : 60; // condensed = 32px, normal = 60px
  };

  // Zkontrolujeme, jestli je den nebo čas blokovaný absencí a vrátíme název absence
  const getBlockingAbsence = (day: dayjs.Dayjs, time?: string, staffName?: string) => {
    const dayString = day.format('YYYY-MM-DD');

    for (const absence of absences) {
      // Jakákoliv absence blokuje - bez ohledu na typ nebo isActive

      const absenceStart = dayjs(`${absence.startDate}T${absence.startTime}`);
      const absenceEnd = dayjs(`${absence.endDate}T${absence.endTime}`);
      const dayStart = day.startOf('day');
      const dayEnd = day.endOf('day');

      // Zkontrolujeme, jestli absence pokrývá celý den
      if ((absenceStart.isBefore(dayStart) || absenceStart.isSame(dayStart)) && (absenceEnd.isAfter(dayEnd) || absenceEnd.isSame(dayEnd))) {
        // Absence pokrývá celý den - zkontrolujeme, jestli se týká všech zaměstnanců nebo konkrétního
        if (!absence.userId || !staffName) {
          return absence; // Absence pro všechny zaměstnance
        }

        // Zkontrolujeme, jestli se absence týká konkrétního zaměstnance
        if (absence.userName === staffName) {
          return absence;
        }
      }

      // Pokud je zadán konkrétní čas, zkontrolujeme, jestli je v rozsahu absence
      if (time && staffName) {
        const slotDateTime = dayjs(`${dayString}T${time}`);

        if ((slotDateTime.isAfter(absenceStart) || slotDateTime.isSame(absenceStart)) && slotDateTime.isBefore(absenceEnd)) {
          // Slot je v rozsahu absence - zkontrolujeme zaměstnance
          if (!absence.userId) {
            return absence; // Absence pro všechny zaměstnance
          }

          if (absence.userName === staffName) {
            return absence;
          }
        }
      }
    }

    return null;
  };

  // Určíme velikost textu podle display mode
  // const getTextSize = () => {
  //   return displayMode === 'condensed' ? 'text-xs' : 'text-xs';
  // };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Načítání rezervací...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 min-h-screen">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Rezervace pro {dayjs(selectedDate).format('D. MMMM YYYY')} ({dayjs(selectedDate).format('dddd')})
          </h3>

          <div className="flex items-center space-x-4">
            {/* Režim zobrazení */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => onViewModeChange?.('day')}
                className={`px-3 py-1 text-sm rounded transition-colors ${viewMode === 'day' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Den
              </button>
              <button
                onClick={() => onViewModeChange?.('week')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  viewMode === 'week' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Týden
              </button>
              <button
                onClick={() => onViewModeChange?.('fullWeek')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  viewMode === 'fullWeek' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Celý týden
              </button>
            </div>

            {/* Display mode přepínač */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => onDisplayModeChange?.('normal')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  displayMode === 'normal' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Normální
              </button>
              <button
                onClick={() => onDisplayModeChange?.('condensed')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  displayMode === 'condensed' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Kompaktní
              </button>
            </div>

            {/* Navigace */}
            <div className="flex items-center space-x-2">
              <button onClick={() => onDateChange?.('prev')} className="p-2 hover:bg-gray-100 rounded-lg" disabled={isLoading}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button onClick={() => onDateChange?.('next')} className="p-2 hover:bg-gray-100 rounded-lg" disabled={isLoading}>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <button onClick={onTodayClick} className="text-sm text-green-600 hover:text-green-800 px-2 py-1" disabled={isLoading}>
                Dnes
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="calendar-container">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Header s dny a zaměstnanci */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex">
              <div className="w-24 p-3 border-r border-gray-200 flex items-center justify-center">
                <div className="text-sm font-medium text-gray-700 transform -rotate-90 whitespace-nowrap">Čas</div>
              </div>

              {/* Kombinace dnů a zaměstnanců */}
              {displayDays.map((day, dayIndex) => {
                const dayStyle = getDayStyle(day);
                return (
                  <div key={dayIndex} className={`flex-1 border-r border-gray-200`}>
                    <div className={`p-2 text-center ${dayStyle.headerBg} border-b ${dayStyle.borderColor}`}>
                      <div className={`text-sm font-medium ${dayStyle.headerText}`}>{day.format('D. MMM')}</div>
                      <div className={`text-xs ${dayStyle.headerText}`}>{day.format('dddd')}</div>
                      <div className={`text-xs ${dayStyle.statusColor} font-medium mt-1`}>{dayStyle.statusText}</div>
                    </div>

                    {/* Zaměstnanci pro tento den - každý má vlastní sloupec */}
                    <div className="flex">
                      {displayEmployees.map(employee => (
                        <div key={`${dayIndex}-${employee.id}`} className="flex-1 p-2 border-r border-gray-100 text-center">
                          <div className="text-xs font-medium text-gray-700">{employee.displayName}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Časové řádky s rezervacemi */}
          <div>
            {timeSlots.map((slot, index) => (
              <div key={slot.time} className={`border-b border-gray-100 ${!slot.isWithinWorkingHours ? 'bg-gray-100' : ''}`}>
                <div className="flex" style={{ minHeight: `${getRowHeight()}px` }}>
                  {/* Časový label */}
                  <div className={`w-24 p-2 border-r border-gray-200 flex items-center justify-center ${!slot.isWithinWorkingHours ? 'bg-gray-200' : 'bg-gray-50'}`}>
                    <div className={`text-xs ${!slot.isWithinWorkingHours ? 'text-gray-400' : 'text-gray-600'}`}>{index % 4 === 0 ? slot.displayTime : ''}</div>
                  </div>

                  {/* Sloupce pro dny a zaměstnance */}
                  {displayDays.map((day, dayIndex) => (
                    <div key={dayIndex} className="flex-1 border-r border-gray-100">
                      {/* Zaměstnanci pro tento den - každý má vlastní sloupec */}
                      <div className="flex">
                        {displayEmployees.map(employee => {
                          if (!employee.displayName) return null;

                          // Zkontrolujeme, jestli je tento den v otevíracích hodinách
                          const dayOfWeek = day.day();
                          // Převod z dayjs formátu (0-6) na API formát (1-7)
                          const apiWeekday = dayOfWeek === 0 ? 7 : dayOfWeek;
                          const dayWorkingHours = workingHours.filter(wh => wh.weekday === apiWeekday && wh.isActive);
                          const isDayOpen = dayWorkingHours.length > 0;

                          // Zkontrolujeme, jestli je tento konkrétní čas v otevíracích hodinách pro tento den
                          const isSlotWithinWorkingHours = dayWorkingHours.some(wh =>
                            wh.intervals?.some(interval => {
                              if (!interval.startTime || !interval.endTime || !interval.isActive) return false;

                              const [startHour, startMinute] = interval.startTime.split(':').map(Number);
                              const [endHour, endMinute] = interval.endTime.split(':').map(Number);

                              const slotTimeMinutes = slot.hour * 60 + slot.minute;
                              const startTimeMinutes = startHour * 60 + startMinute;
                              const endTimeMinutes = endHour * 60 + endMinute;

                              return slotTimeMinutes >= startTimeMinutes && slotTimeMinutes < endTimeMinutes;
                            }),
                          );

                          // Zkontrolujeme, jestli je slot blokovaný absencí
                          const blockingAbsence = getBlockingAbsence(day, slot.time, employee.displayName);

                          // Debug logování pro sloty mezi 17:00-19:00
                          if (slot.hour >= 17 && slot.hour < 19) {
                            console.log(`🔍 Debug slot ${slot.time} pro den ${day.format('DD.MM')}:`, {
                              isDayOpen,
                              isSlotWithinWorkingHours,
                              blockingAbsence: blockingAbsence?.name,
                              dayWorkingHours: dayWorkingHours.map(wh => ({
                                weekday: wh.weekday,
                                intervals: wh.intervals?.map(i => `${i.startTime}-${i.endTime}`),
                              })),
                            });
                          }

                          const employeeBookings = getBookingsForEmployee(employee.displayName, day);
                          const slotBookings = employeeBookings.filter(booking => {
                            if (!booking.start) return false;

                            const startTime = formatTime(booking.start);
                            const [startHour, startMinute] = startTime.split(':').map(Number);
                            const slotHour = slot.hour;
                            const slotMinute = slot.minute;

                            // Zkontroluj, jestli rezervace začíná v tomto slotu
                            return startHour === slotHour && startMinute === slotMinute;
                          });

                          // Zkontrolujeme, jestli je slot dostupný pro rezervace
                          const isSlotInPast =
                            day.isBefore(dayjs(), 'day') ||
                            (day.isSame(dayjs(), 'day') && slot.hour < dayjs().hour()) ||
                            (day.isSame(dayjs(), 'day') && slot.hour === dayjs().hour() && slot.minute <= dayjs().minute());
                          const isSlotAvailable =
                            isDayOpen && (viewMode === 'day' ? slot.isWithinWorkingHours : isSlotWithinWorkingHours) && !blockingAbsence && !isSlotInPast;
                          const cellStyle = getCellStyle(isSlotAvailable);

                          return (
                            <div key={`${dayIndex}-${employee.id}`} className={`flex-1 relative border-r ${cellStyle}`} style={{ minHeight: `${getRowHeight()}px` }}>
                              {/* Klikatelná oblast pro prázdné místo - pouze pokud je slot dostupný */}
                              {isSlotAvailable && (
                                <div
                                  className={`absolute inset-0 cursor-pointer hover:bg-green-50 hover:border-green-200 border border-transparent transition-all duration-200 group ${
                                    dragOverSlot?.time === slot.time &&
                                    dragOverSlot?.staffName === employee.displayName &&
                                    dragOverSlot?.day.format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
                                      ? 'bg-blue-100 border-blue-300'
                                      : ''
                                  }`}
                                  onClick={() => {
                                    if (onEmptySlotClick) {
                                      onEmptySlotClick(slot.time, employee.displayName);
                                    }
                                  }}
                                  onDragOver={e => handleDragOver(e, slot.time, employee.displayName, day)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={e => handleDrop(e, slot.time, employee.displayName, day)}
                                  title={`Klikněte pro vytvoření rezervace v ${slot.time} pro ${employee.displayName}`}
                                >
                                  {/* Prázdné místo - zobrazí se pouze při hover */}
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center h-full">
                                    <div className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">+ Nová rezervace</div>
                                  </div>

                                  {/* Drop zone indikátor */}
                                  {dragOverSlot?.time === slot.time &&
                                    dragOverSlot?.staffName === employee.displayName &&
                                    dragOverSlot?.day.format('YYYY-MM-DD') === day.format('YYYY-MM-DD') && (
                                      <div className="absolute inset-0 bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center">
                                        <div className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium">
                                          {draggedBooking && draggedBooking.worker?.displayName !== employee.displayName
                                            ? `Přesunout k ${employee.displayName}`
                                            : 'Přesunout sem'}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              )}

                              {/* Indikátor, že není otevřeno - pouze pokud je den skutečně zavřený */}
                              {!isDayOpen && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="text-gray-400 text-xs">Zavřeno</div>
                                </div>
                              )}

                              {/* Indikátor pro sloty v minulosti */}
                              {isSlotInPast && <div className="absolute inset-0 cursor-not-allowed" title="Minulost" />}

                              {/* Indikátor pro sloty mimo pracovní hodiny v otevřený den */}
                              {isDayOpen && !isSlotWithinWorkingHours && !isSlotInPast && <div className="absolute inset-0 cursor-not-allowed" title="Přestávka" />}

                              {/* Indikátor, že je slot blokovaný absencí */}
                              {isDayOpen && isSlotWithinWorkingHours && blockingAbsence && (
                                <div className="absolute inset-0 cursor-not-allowed" title={blockingAbsence.name} />
                              )}

                              {/* Existující rezervace */}
                              {slotBookings.map(booking => {
                                if (!booking.start || !booking.end) return null;

                                const startTime = formatTime(booking.start);
                                const endTime = formatTime(booking.end);
                                const [startHour, startMinute] = startTime.split(':').map(Number);
                                const [endHour, endMinute] = endTime.split(':').map(Number);

                                const duration = endHour * 60 + endMinute - (startHour * 60 + startMinute);
                                const rowHeight = getRowHeight();
                                const height = Math.max(rowHeight, (duration / 15) * rowHeight); // Každých 15 minut = rowHeight px

                                return (
                                  <div
                                    key={booking.id}
                                    draggable
                                    onDragStart={e => handleDragStart(e, booking)}
                                    onDragEnd={handleDragEnd}
                                    className={`absolute ${getBookingColor(
                                      booking,
                                    )} text-white rounded border cursor-move hover:opacity-90 transition-all duration-200 shadow-sm w-full z-10 ${
                                      draggedBooking?.id === booking.id ? 'opacity-50 scale-95 shadow-lg' : ''
                                    }`}
                                    style={{
                                      height: `${height}px`,
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      backgroundColor: getServiceColor(booking) || undefined,
                                      borderColor: getServiceColor(booking) || undefined,
                                    }}
                                    onClick={e => {
                                      e.stopPropagation(); // Zabráníme kliknutí na prázdné místo
                                      // Zde můžete přidat modal nebo detail rezervace
                                    }}
                                    title={`${booking.bookingLines?.[0]?.nameSnapshot || 'Služba'} - ${booking.customer?.firstName || ''} ${
                                      booking.customer?.lastName || ''
                                    } - ${booking.totalAmount || 0} Kč (${startTime} - ${endTime}) - Přetáhněte pro přesun`}
                                  >
                                    <div className={`${displayMode === 'condensed' ? 'p-1' : 'p-2'} h-full flex flex-col justify-center`}>
                                      {displayMode === 'condensed' ? (
                                        <>
                                          <div className="font-semibold text-xs truncate">
                                            {booking.customer?.firstName || ''} {booking.customer?.lastName || ''}
                                          </div>
                                          <div className="text-xs opacity-90 truncate">{booking.bookingLines?.[0]?.nameSnapshot || 'Služba'}</div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="font-semibold text-xs truncate">
                                            {booking.customer?.firstName || ''} {booking.customer?.lastName || ''}
                                          </div>
                                          <div className="text-xs opacity-90 truncate">{booking.bookingLines?.[0]?.nameSnapshot || 'Služba'}</div>
                                          <div className="text-xs font-bold text-yellow-200">{booking.totalAmount || 0} Kč</div>
                                          <div className="text-xs opacity-75 mt-1">
                                            {startTime} - {endTime}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Informace o barvách služeb a drag & drop */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-600 space-y-2">
          <div>
            <span className="font-medium">💡 Tip:</span> Barvy rezervací odpovídají kategoriím služeb
          </div>
          <div>
            <span className="font-medium">🖱️ Drag & Drop:</span> Přetáhněte rezervace pro jejich přesun na jiný čas, den nebo k jinému zaměstnanci
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomBookingCalendar;
